/**
 * Unlock Content Service
 * Flutter: lib/presentation/screens/content/content_view_model.dart
 *
 * Encapsulates all business logic for content unlock access control and the
 * unlock transaction flow. All methods mirror Flutter's content_view_model.dart
 * exactly — field names, enum values, and logic conditions must stay in sync.
 */
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { AppTransactionType } from '@/domain/enums/transaction/AppTransactionType';
import { AppNotificationType } from '@/data/enums/notification/appNotificationType';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { UnlockedContentStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface HasExclusiveRightsParams {
  isOwner: boolean;
  hasUnlockedContent: boolean;
  hasActiveSubscription: boolean;
  typeLimit: number | undefined | null;
  duration: number | undefined | null;
}

export interface CanUnlockContentParams extends HasExclusiveRightsParams {
  allowUnlock: boolean;
  unlockCharge: number | undefined | null;
}

export class UnlockContentService {
  private readonly storage = SecureStorageWebCrypto.getInstance();

  /**
   * Whether the user has full exclusive access to this content.
   * Flutter: ContentViewModel._hasExclusiveRights()
   */
  hasExclusiveRights(params: HasExclusiveRightsParams): boolean {
    const { isOwner, hasUnlockedContent, hasActiveSubscription, typeLimit, duration } = params;
    return (
      isOwner ||
      hasUnlockedContent ||
      hasActiveSubscription ||
      (typeLimit != null && duration != null && typeLimit === duration)
    );
  }

  /**
   * Whether the unlock action can be performed.
   * Flutter: ContentViewModel.canUnlockContent
   */
  canUnlockContent(params: CanUnlockContentParams): boolean {
    const { allowUnlock, unlockCharge, isOwner, hasUnlockedContent, hasActiveSubscription, typeLimit, duration } = params;
    return (
      allowUnlock &&
      unlockCharge != null &&
      !isOwner &&
      !hasUnlockedContent &&
      !hasActiveSubscription &&
      !(typeLimit != null && duration != null && typeLimit === duration)
    );
  }

  /**
   * Reads SecureStorage to check if a specific piece of content has been unlocked.
   * Not used currently in the service itself, but exposed for use in the feature hook to compute exclusive rights flags.
   * Flutter: ExclusiveContentNotifier.hasUnlockedContent(mediaRelativePath)
    *    
   */
  async hasUnlockedContent(mediaRelativePath: string): Promise<boolean> {
    const key = SECURE_STORAGE_KEYS.UNLOCKED_CONTENT + mediaRelativePath;
    const stored = await this.storage.getEncrypted<UnlockedContentStorage>(key);
    return stored !== null && stored.path === mediaRelativePath;
  }

  /**
   * Executes the unlock transaction:
   *   1. Builds AppTransaction + notification DTOs matching Flutter exactly.
   *   2. Calls the `sendGift` cloud function.
   *
   * Flutter: UnlockContentBottomSheet._unlockVideo()
   */
  async executeUnlock(
    media: UserMediaEntity,
    currentUser: UserEntity,
  ): Promise<boolean> {
    if (!media.unlockCharge || !media.relativePath || !media.firebaseId) {
      appLogger.error('executeUnlock called with incomplete media', 'UnlockContentService.executeUnlock', {
        error: new Error('Missing required media fields'),
        context: { unlockCharge: media.unlockCharge, relativePath: media.relativePath, firebaseId: media.firebaseId },
      });
      return false;
    }

    const now = Date.now();

    const giftInfo = {
      name: 'Content Unlock',
      coins: media.unlockCharge,
      thumbnail: '',
      instances: 1,
    };

    const mediaInfo = {
      mediaId: media.id,
      allowUnlock: media.allowUnlock,
      unlockCharge: media.unlockCharge,
      aspectRatio: media.aspectRatio,
      duration: media.duration,
      thumbnail: media.relativeThumbnailPath ?? '',
      path: media.relativePath ?? '',
      firebaseId: media.firebaseId,
      type: media.type ?? 0,
    };

    const usersInfo = {
      userId: currentUser.firebaseId,
      peerUserId: media.firebaseId,
      usersIds: [currentUser.firebaseId, media.firebaseId],
      name: currentUser.name,
      username: currentUser.username,
      peerName: media.userName ?? '',
    };

    const appTransaction = {
      usersInfo,
      transactionType: AppTransactionType.unlockContent,
      mediaInfo,
      giftInfo,
      createdDate: now,
      updatedDate: now,
    };

    const notification = {
      info: {
        notificationType: AppNotificationType.unlockContent,
        giftInfo,
        mediaInfo,
        usersInfo,
        timestamp: now,
      },
    };

    const success = await cloudFunctionsService.sendGift(appTransaction, notification, null);

    return success;
  }
}

export const unlockContentService = new UnlockContentService();
